import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import blipService from '../../services/blipService';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMic, FiSquare, FiArrowLeft, FiAward, FiStar, FiRefreshCw, FiSettings, FiLoader } = FiIcons;

function ReadingChallengeGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [accuracy, setAccuracy] = useState(null);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const readingTexts = {
    5: "The cat sat on the mat.",
    6: "I see a big red apple in the tree.",
    7: "The hungry lion is looking for a tasty snack in the jungle.",
    8: "Little birds sing happy songs when the sun comes up every morning.",
    9: "Plants use the energy from the sun to grow and create oxygen for us to breathe.",
    10: "The solar system has eight planets that travel around the sun in a big circle.",
    11: "Photosynthesis is the amazing process where green plants transform light into food.",
    12: "Gravitational forces keep the planets in orbit and ensure everything stays grounded on Earth."
  };

  const targetText = readingTexts[state.age] || readingTexts[10];

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = processAudio;
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setError(null);
      setAccuracy(null);
      setTranscription('');
    } catch (err) {
      setError("Please allow microphone access to start your quest!");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
  };

  const calculateAccuracy = (original, transcribed) => {
    const clean = (str) => str.toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "").trim().split(/\s+/);
    const origWords = clean(original);
    const transWords = clean(transcribed);
    
    let matches = 0;
    origWords.forEach(word => {
      if (transWords.includes(word)) matches++;
    });

    return Math.round((matches / origWords.length) * 100);
  };

  const processAudio = async () => {
    setIsProcessing(true);
    const audioBlob = new Blob(chunksRef.current, { type: 'audio/wav' });
    
    try {
      const text = await blipService.transcribe(audioBlob);
      setTranscription(text);
      
      const acc = calculateAccuracy(targetText, text);
      setAccuracy(acc);

      if (acc >= 70) {
        // Successful reading
        dispatch({ type: 'ADD_SCORE', payload: acc });
        dispatch({ 
          type: 'GAME_COMPLETED', 
          payload: { questId: 'reading-challenge', bonusTokens: acc >= 90 ? 50 : 20 } 
        });
      } else {
        dispatch({ type: 'ADD_WRONG_ANSWER' });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const reset = () => {
    setTranscription('');
    setAccuracy(null);
    setError(null);
    setIsProcessing(false);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="min-h-screen p-4 flex flex-col items-center justify-center bg-gradient-to-br from-cyan-400 to-blue-600"
    >
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="bg-white/20 p-3 rounded-2xl text-white hover:bg-white/30 transition-colors shadow-lg"
          >
            <SafeIcon icon={FiArrowLeft} className="text-2xl" />
          </button>
          <h1 className="text-3xl font-kid text-white drop-shadow-md">Reading Quest 📖</h1>
          <button 
            onClick={() => navigate('/settings')} 
            className="bg-white/20 p-3 rounded-2xl text-white hover:bg-white/30 transition-colors shadow-lg"
          >
            <SafeIcon icon={FiSettings} className="text-2xl" />
          </button>
        </div>

        {/* Game Card */}
        <div className="bg-white rounded-[3rem] p-8 md:p-12 shadow-2xl border-b-[12px] border-blue-200 relative overflow-hidden">
          <div className="text-center mb-10">
            <div className="inline-block bg-blue-50 px-6 py-2 rounded-full text-sm font-comic text-blue-600 uppercase tracking-widest mb-6 font-bold">
              Read this aloud!
            </div>
            <motion.h2 
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="text-2xl md:text-4xl font-kid text-blue-900 leading-relaxed px-4"
            >
              "{targetText}"
            </motion.h2>
          </div>

          <div className="flex flex-col items-center gap-8">
            <AnimatePresence mode="wait">
              {accuracy === null ? (
                <motion.div 
                  key="recorder" 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="flex flex-col items-center"
                >
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isProcessing}
                    className={`w-32 h-32 rounded-full shadow-2xl flex items-center justify-center text-5xl transition-all border-8 border-white ${
                      isRecording ? 'bg-red-500 animate-pulse' : isProcessing ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'
                    } text-white`}
                  >
                    {isProcessing ? (
                      <SafeIcon icon={FiLoader} className="animate-spin" />
                    ) : (
                      <SafeIcon icon={isRecording ? FiSquare : FiMic} />
                    )}
                  </motion.button>
                  
                  <p className="mt-6 font-comic text-blue-600 font-bold text-lg">
                    {isRecording ? "I'm listening... Speak clearly!" : isProcessing ? "Thinking about what you said..." : "Tap the mic and start reading!"}
                  </p>
                </motion.div>
              ) : (
                <motion.div 
                  key="results" 
                  initial={{ opacity: 0, scale: 0.9 }} 
                  animate={{ opacity: 1, scale: 1 }}
                  className="w-full text-center"
                >
                  <div className={`text-8xl mb-4 ${accuracy >= 70 ? 'animate-bounce' : ''}`}>
                    {accuracy >= 90 ? '🦁' : accuracy >= 70 ? '🦒' : '🐢'}
                  </div>
                  
                  <h3 className={`text-3xl font-kid mb-4 ${accuracy >= 70 ? 'text-green-600' : 'text-orange-500'}`}>
                    {accuracy >= 90 ? 'Perfect Reading!' : accuracy >= 70 ? 'Great Job!' : 'Keep Practicing!'}
                  </h3>

                  <div className="bg-blue-50 rounded-3xl p-6 mb-8 border-2 border-blue-100 shadow-inner">
                    <div className="flex justify-between items-center mb-3 px-2">
                      <span className="font-comic text-blue-700 font-bold">Explorer Accuracy</span>
                      <span className="font-kid text-3xl text-blue-900">{accuracy}%</span>
                    </div>
                    
                    <div className="h-8 bg-blue-100 rounded-full overflow-hidden border-2 border-white shadow-inner">
                      <motion.div 
                        initial={{ width: 0 }} 
                        animate={{ width: `${accuracy}%` }} 
                        transition={{ duration: 1, ease: "easeOut" }}
                        className={`h-full ${accuracy >= 90 ? 'bg-green-500' : accuracy >= 70 ? 'bg-yellow-500' : 'bg-orange-500'}`} 
                      />
                    </div>
                    
                    {accuracy >= 70 && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1 }}
                        className="mt-4 flex items-center justify-center gap-2 text-green-600 font-comic font-bold"
                      >
                        <SafeIcon icon={FiAward} />
                        <span>You earned tokens for your journey!</span>
                      </motion.div>
                    )}
                  </div>

                  <div className="mb-8">
                    <p className="text-xs font-comic text-gray-400 uppercase tracking-widest mb-2">You said:</p>
                    <div className="text-gray-600 font-comic italic text-lg bg-gray-50 p-6 rounded-3xl border-2 border-dashed border-gray-200">
                      "{transcription || "..."}"
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button 
                      onClick={reset} 
                      className="flex-1 bg-blue-500 text-white p-5 rounded-[2rem] font-kid text-xl shadow-lg hover:bg-blue-600 transition-all flex items-center justify-center gap-2 border-b-4 border-blue-700"
                    >
                      <SafeIcon icon={FiRefreshCw} /> Try Again
                    </button>
                    <button 
                      onClick={() => navigate('/dashboard')} 
                      className="flex-1 bg-white border-4 border-blue-100 text-blue-900 p-5 rounded-[2rem] font-kid text-xl shadow-md hover:bg-blue-50 transition-all"
                    >
                      Finish
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {error && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }} 
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border-4 border-red-200 text-red-600 p-6 rounded-[2rem] font-comic text-center w-full shadow-lg"
              >
                <div className="text-3xl mb-2">⚠️</div>
                <p className="font-bold text-lg">{error}</p>
                <button 
                  onClick={() => navigate('/settings')} 
                  className="mt-4 bg-red-500 text-white px-8 py-3 rounded-xl font-kid hover:bg-red-600 transition-all shadow-md"
                >
                  Fix in Settings
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default ReadingChallengeGame;